import React from "react";
import styled from "styled-components";
import { IconButton } from "@mui/material";
import { AndGroup, GroupingType } from "../../../rule-engine-core/Expressions/Expressions/Logic/AndGroup";
import { ValidationIssue, ValidationLevel } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { BaseTestResultData } from "../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { AutoBind } from "../../../rule-engine-core/Common/AutoBind";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ExpressionTestResultColorContext } from "../../Testing/ExpressionTestResultColorContext";
import { CompactColor, ExpressionReactContext, ExpressionRenderType } from "../../Structure/ExpressionReactContext";
import { ExpressionRuleEditor } from "../ExpressionRuleEditor";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { ExpressionLineDiv, ExpressionPiece } from "../../Builder/ExpressionStyles";
import { PlusIcon } from "../../../CommonUI/Icon/PlusIcon";
import { InlineOptionSelect } from "../../../CommonUI/InlineEditor/InlineOptionSelect";
import { DeleteIcon } from "../../../CommonUI/Icon/DeleteIcon";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { ExpressionValueRenderer } from "../../Builder/ExpressionValueRenderer";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../../rule-engine-core/Common/AppGlueAny";
import { ExpressionValueType } from "../../../rule-engine-core/Expressions/ExpressionValueType";

const StyledBracketedDiv = styled.div<{
    renderType: ExpressionRenderType;
    hasChild?: boolean;
    direction?: "row" | "column";
    compactColor?: CompactColor;
    issueType?: ValidationLevel;
    color?: string;
}>`
    position: relative;
    display: flex;
    align-items: center;

    flex-direction: ${(props) => props.direction || "row"};

    ${(props) =>
        props.renderType === ExpressionRenderType.FULL
            ? `
                padding: 2px 4px;
                margin-left: 6px;
                margin-right: 10px;

                ${
                    props.hasChild
                        ? `
                            flex: 1;
                        `
                        : ``
                }

                &::before {
                    content: "";
                    width: 4px;
                    position: absolute;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    border: 1px solid ${props.color || UIStyleConstants.BLUE_LIGHTER_COLOR};
                    border-right: none;
                    border-top-left-radius: 3px;
                    border-bottom-left-radius: 3px;
                }

                &::after {
                    content: "";
                    width: 4px;
                    position: absolute;
                    top: 0;
                    right: 0;
                    bottom: 0;
                    border: 1px solid ${props.color || UIStyleConstants.BLUE_LIGHTER_COLOR};
                    border-left: none;
                    border-top-right-radius: 3px;
                    border-bottom-right-radius: 3px;
                }

            `
            : `
                padding: 0;
            `}
`;

export const GroupedBracketedDiv = (props: {
    children?: AppGlueAny;
    hasChild?: boolean;
    direction?: "row" | "column";
    issue?: ValidationIssue;
}) => {
    const context = React.useContext(ExpressionReactContext);
    const testcolor = React.useContext(ExpressionTestResultColorContext);

    return (
        <StyledBracketedDiv
            {...props}
            renderType={context.renderType}
            compactColor={context.color}
            issueType={props.issue?.level}
            color={testcolor}
        />
    );
};

export class AndGroupEditor extends ExpressionRuleEditor<AndGroup> {
    getTestValueColor(el: string, testcontext: BaseTestResultData | undefined): string | undefined {
        if (!testcontext) return undefined;

        if (!testcontext) return undefined;
        let testitem = testcontext.findItem(el);

        if (testitem?.result === "true") {
            return UIStyleConstants.GREEN_COLOR;
        } else if (testitem?.result === "false") return UIStyleConstants.RED_COLOR;

        return undefined;
    }

    render() {
        return (
            <ObserveState
                listenTo={this.ruleData}
                control={() => (
                    <GroupedBracketedDiv
                        issue={this.ruleData!.getValidationIssues()[0]}
                        direction={"column"}
                    >
                        {this.ruleData!.elements.map((g, i) => {
                            return (
                                <TestContext.Consumer key={g._id}>
                                    {(testcontext) => (
                                        <ExpressionTestResultColorContext.Provider
                                            value={this.getTestValueColor(g._id, testcontext)}
                                        >
                                            <ExpressionLineDiv
                                                key={i}
                                                data-testid="ExpressionGroup-row"
                                            >
                                                <ExpressionValueRenderer
                                                    key={"g-" + i}
                                                    el={g}
                                                />
                                                {this.renderEndOfLine(i)}
                                            </ExpressionLineDiv>
                                        </ExpressionTestResultColorContext.Provider>
                                    )}
                                </TestContext.Consumer>
                            );
                        })}
                    </GroupedBracketedDiv>
                )}
            />
        );
    }

    @AutoBind
    private addToFromAction(event: React.MouseEvent<HTMLElement>) {
        event.stopPropagation();
        this.ruleData!.add();
    }

    @AutoBind
    private deleteToFromAction(index: number) {
        this.ruleData!.delete(index);
    }

    private canDeleteOrClear(index: number): boolean {
        return (
            this.ruleData!.elements.length > 1 ||
            this.ruleData!.elements[index]?.valueType !== ExpressionValueType.UNSET
        );
    }

    private renderDeleteButton(index: number) {
        if (!this.canDeleteOrClear(index)) return null;

        return (
            <IconButton
                size={"small"}
                aria-label="delete"
                onClick={(event: React.MouseEvent<HTMLElement>) => {
                    event.stopPropagation();
                    this.deleteToFromAction(index);
                }}
            >
                <DeleteIcon />
            </IconButton>
        );
    }

    @AutoBind
    private onGroupTypeSelect(newGroup: string) {
        this.ruleData!.typeOfGroup = newGroup as GroupingType;

        StateManager.changed(this);
    }

    private renderEndOfLine(i: number) {
        return (
            <TestContext.Consumer>
                {(testcontext) => {
                    if (testcontext) return null;
                    let isLast = i + 1 === this.ruleData!.elements.length;
                    let isFirst = i === 0;
                    if (isFirst && isLast) {
                        return (
                            <ExpressionPiece data-testid="ExpressionGroup-add">
                                <IconButton
                                    size={"small"}
                                    onClick={this.addToFromAction}
                                >
                                    <PlusIcon />
                                </IconButton>
                                {this.renderDeleteButton(i)}
                            </ExpressionPiece>
                        );
                    } else if (isFirst && !isLast) {
                        return (
                            <ExpressionPiece data-testid="ExpressionGroup-add">
                                <ExpressionReactContext.Consumer>
                                    {(context) => (
                                        <InlineOptionSelect
                                            text={this.ruleData!.typeOfGroup}
                                            options={[GroupingType.AND, GroupingType.OR]}
                                            onEdit={this.onGroupTypeSelect}
                                            readOnly={
                                                context.renderType === ExpressionRenderType.COMPACT ||
                                                context.renderType === ExpressionRenderType.TEXT
                                            }
                                        />
                                    )}
                                </ExpressionReactContext.Consumer>
                                {this.renderDeleteButton(i)}
                            </ExpressionPiece>
                        );
                    } else if (!isLast) {
                        return (
                            <ExpressionPiece data-testid="ExpressionGroup-add">
                                <ExpressionReactContext.Consumer>
                                    {(context) => (
                                        <InlineOptionSelect
                                            text={this.ruleData!.typeOfGroup}
                                            options={[GroupingType.AND, GroupingType.OR]}
                                            onEdit={this.onGroupTypeSelect}
                                            readOnly={
                                                context.renderType === ExpressionRenderType.COMPACT ||
                                                context.renderType === ExpressionRenderType.TEXT
                                            }
                                        />
                                    )}
                                </ExpressionReactContext.Consumer>

                                {this.renderDeleteButton(i)}
                            </ExpressionPiece>
                        );
                    } else if (isLast) {
                        return (
                            <ExpressionPiece>
                                <IconButton
                                    size={"small"}
                                    onClick={this.addToFromAction}
                                >
                                    <PlusIcon />
                                </IconButton>
                                {this.renderDeleteButton(i)}
                            </ExpressionPiece>
                        );
                    }
                }}
            </TestContext.Consumer>
        );
    }
}
